import { useMemo, useState } from"react";
import { Search, BookMarked } from"lucide-react";
import { ECRITURES, CYCLES, TOTAL_TVA } from"@/data/ecritures";
import CopyEcritureButton from"@/components/CopyEcritureButton";

function norm(s) {
 return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"");
}

export default function Ecritures({ onAccountClick }) {
 const [q, setQ] = useState("");
 const [cycle, setCycle] = useState("Tous");
 const [openId, setOpenId] = useState(ECRITURES[0]?.id || null);

 const filtered = useMemo(() => {
 const nq = norm(q.trim());
 return ECRITURES.filter((e) => {
 if (cycle !=="Tous" && e.cycle !== cycle) return false;
 if (!nq) return true;
 return (
 norm(e.titre).includes(nq) ||
 norm(e.situation).includes(nq) ||
 norm(e.commentaire).includes(nq) ||
 e.lignes.some((l) => l.num.includes(nq) || norm(l.lib).includes(nq))
 );
 });
 }, [q, cycle]);

 return (
 <div data-testid="ecritures-view" className="space-y-6">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <BookMarked className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Catalogue d&apos;écritures
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Schémas d&apos;écritures types
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 <strong>{ECRITURES.length} modèles</strong> classiques pour vous
 guider sur les opérations courantes : achats, ventes, paie, TVA,
 immobilisations, clôture.
 </p>
 </div>
 </div>

 {/* Search */}
 <div className="mt-6 relative max-w-xl">
 <Search
 size={16}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
 />
 <input
 data-testid="ecritures-search"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="Rechercher (ex. amortissement, FNP, TVA…)"
 className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-keyto bg-white text-sm font-medium text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 </div>

 {/* Filter cycle */}
 <div className="mt-4 flex flex-wrap gap-2">
 {["Tous", ...CYCLES.map((c) => c.id)].map((c) => {
 const meta = CYCLES.find((x) => x.id === c);
 const isActive = cycle === c;
 return (
 <button
 key={c}
 data-testid={`ecritures-filter-${c}`}
 onClick={() => setCycle(c)}
 className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
 isActive
 ?"text-white border-transparent"
 :"text-slate-600 bg-white border-keyto hover:border-slate-400"
 }`}
 style={isActive ? { background: meta?.color ||"#15294F" } : {}}
 >
 {c}
 </button>
 );
 })}
 </div>
 </div>

 {/* Liste */}
 <div className="space-y-3">
 {filtered.length === 0 && (
 <div className="border border-dashed border-keyto rounded-xl bg-white p-8 text-center text-slate-500">
 Aucune écriture ne correspond à votre recherche.
 </div>
 )}
 {filtered.map((e) => {
 const meta = CYCLES.find((c) => c.id === e.cycle);
 const color = meta?.color ||"#15294F";
 const isOpen = openId === e.id;
 const total = TOTAL_TVA(e.lignes);

 return (
 <article
 key={e.id}
 data-testid={`ecriture-${e.id}`}
 className="bg-white border border-keyto rounded-xl shadow-keyto-sm overflow-hidden"
 style={{ borderLeft: `4px solid ${color}` }}
 >
 <button
 onClick={() => setOpenId(isOpen ? null : e.id)}
 className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 transition"
 >
 <span
 className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded"
 style={{ background: color +"15", color }}
 >
 {e.cycle}
 </span>
 <h2 className="flex-1 font-heading font-semibold text-[#15294F]">
 {e.titre}
 </h2>
 <span className="text-xs text-slate-400 font-mono">
 {e.lignes.length} lignes
 </span>
 <span
 className="text-slate-400 text-xl transition-transform"
 style={{ transform: isOpen ?"rotate(90deg)" :"none" }}
 >
 ›
 </span>
 </button>

 {isOpen && (
 <div className="px-5 pb-5 pt-1 border-t border-keyto bg-slate-50/50">
 {/* Situation */}
 <div className="mb-4 pl-3 border-l-2 italic text-sm text-slate-600" style={{ borderColor: color }}>
 <span className="not-italic font-semibold text-slate-700">Situation : </span>
 {e.situation}
 </div>

 {/* Écriture */}
 <div className="overflow-x-auto rounded-lg border border-keyto bg-white">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-100 text-[#15294F]">
 <th className="text-left px-3 py-2 font-semibold">N°</th>
 <th className="text-left px-3 py-2 font-semibold">Libellé</th>
 <th className="text-right px-3 py-2 font-semibold">Débit</th>
 <th className="text-right px-3 py-2 font-semibold">Crédit</th>
 </tr>
 </thead>
 <tbody>
 {e.lignes.map((l, i) => (
 <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
 <td className="px-3 py-2 font-mono font-bold" style={{ color }}>
 <button
 onClick={() => onAccountClick && onAccountClick(l.num)}
 className="hover:underline"
 data-testid={`ecriture-${e.id}-acc-${l.num}`}
 >
 {l.num}
 </button>
 </td>
 <td className="px-3 py-2 text-slate-700">{l.lib}</td>
 <td className="px-3 py-2 text-right font-mono">
 {l.debit ? l.debit.toLocaleString("fr-FR", { minimumFractionDigits: 2 }) :"—"}
 </td>
 <td className="px-3 py-2 text-right font-mono">
 {l.credit ? l.credit.toLocaleString("fr-FR", { minimumFractionDigits: 2 }) :"—"}
 </td>
 </tr>
 ))}
 <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold text-[#15294F]">
 <td colSpan={2} className="px-3 py-2 text-right">Totaux</td>
 <td className="px-3 py-2 text-right font-mono">{total.debit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
 <td className="px-3 py-2 text-right font-mono">{total.credit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
 </tr>
 </tbody>
 </table>
 </div>

 {/* Boutons d'export logiciel comptable */}
 <CopyEcritureButton
 lignes={e.lignes.map((l) => ({ c: l.num, l: l.lib, d: l.debit || 0, k: l.credit || 0 }))}
 titre={e.titre}
 />

 {/* Commentaire */}
 <div className="mt-4 p-4 rounded-lg bg-[#15294F] text-white text-sm leading-relaxed">
 <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color:"#5AA0FF" }}>
 Pourquoi
 </p>
 {e.commentaire}
 </div>

 {/* Variantes */}
 {e.variantes && e.variantes.length > 0 && (
 <div className="mt-3 text-xs text-slate-500">
 <span className="font-semibold text-slate-700">Variantes : </span>
 {e.variantes.join(" ·")}
 </div>
 )}
 </div>
 )}
 </article>
 );
 })}
 </div>
 </div>
 );
}
